import { createSelector } from 'reselect'

import {
  STANDARD_NOTETAKER,
  STANDARD_SCHEDULING,
  TEAMS_NOTETAKER,
  TEAMS_SCHEDULING
} from 'features/billing/modals/plan_selection_modal/config/tiers'
import {
  featureRootSelector
} from 'features/billing/selectors'

const NOTETAKER_PLAN_SELECTION_TIERS = [
  STANDARD_SCHEDULING,
  STANDARD_NOTETAKER,
  TEAMS_SCHEDULING,
  TEAMS_NOTETAKER
]

const checkoutInfoSelector = createSelector(
  featureRootSelector,
  ({ checkoutInfo }) => checkoutInfo
)

export const checkoutInfoLoadingSelector = createSelector(
  checkoutInfoSelector,
  ({ loading }) => loading
)

export const checkoutInfoAdditionalQuantitySelector = createSelector(
  checkoutInfoSelector,
  ({ additionalQuantity }) => additionalQuantity
)

export const checkoutInfoOverrideQuantitySelector = createSelector(
  checkoutInfoSelector,
  ({ overrideQuantity }) => overrideQuantity
)

export const checkoutAvailableCurrenciesSelector = createSelector(
  checkoutInfoSelector,
  ({ currencyOptions }) => currencyOptions
)

export const multipleCurrenciesAvailableSelector = createSelector(
  checkoutAvailableCurrenciesSelector,
  (availableCurrencies) => availableCurrencies.length > 1
)

export const checkoutCountryPostalCodeErrorSelector = createSelector(
  checkoutInfoSelector,
  ({ countryPostalCodeError }) => countryPostalCodeError
)

const checkoutErrorSelector = createSelector(
  checkoutInfoSelector,
  ({ error }) => error
)

export const checkoutErrorPresentSelector = createSelector(
  checkoutErrorSelector,
  (error) => !!error
)

export const checkoutPaymentMethodErrorSelector = createSelector(
  checkoutErrorSelector,
  (error) => error && error.type === 'payment_method' && error.message
)

export const checkoutQuantityErrorSelector = createSelector(
  checkoutErrorSelector,
  (error) => error && error.type === 'quantity' && error.message
)

export const checkoutGeneralErrorSelector = createSelector(
  checkoutErrorSelector,
  (error) => error && !['payment_method', 'quantity', 'postal_code'].includes(error.type) && error.message
)

const detailedCheckoutInfoSelector = createSelector(
  checkoutInfoSelector,
  ({ info }) => info
)

// TODO: Refine this logic when multiple coupons are allowed
export const couponCodeSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({
    addon_coupon: addonCoupon,
    plan_coupon: planCoupon,
    invoice_coupon: invoiceCoupon,
  }) => addonCoupon || planCoupon || invoiceCoupon
)

export const checkoutInfoAvailableCountriesSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ available_countries: availableCountries }) => availableCountries
)

export const addonDiscountTextSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ addon_discount_text: addonDiscountText }) => addonDiscountText
)

export const planDiscountTextSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ plan_discount_text: planDiscountText }) => planDiscountText
)

export const invoiceDiscountTextSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ invoice_discount_text: invoiceDiscountText }) => invoiceDiscountText
)

export const dealPresentSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ deal_present: dealPresent }) => dealPresent
)

export const dealAddonsSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ deal_addons: dealAddons }) => dealAddons || []
)

export const frequencyOptionsSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ frequency_options: frequencyOptions }) => frequencyOptions
)

export const monthlyFrequencyOptionSelector = createSelector(
  frequencyOptionsSelector,
  (frequencyOptions) => frequencyOptions.find(({ frequency }) => frequency === 'month')
)

export const yearlyFrequencyOptionSelector = createSelector(
  frequencyOptionsSelector,
  (frequencyOptions) => frequencyOptions.find(({ frequency }) => frequency === 'year')
)

export const minQuantityRequirementsSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ seat_minimum: seatMinimum, seat_minimum_reason: seatMinimumReason }) => (
    { seatMinimum, seatMinimumReason }
  )
)

export const maxQuantityRequirementsSelector = createSelector(
  detailedCheckoutInfoSelector,
  ({ seat_maximum: seatMaximum }) => (
    { seatMaximum }
  )
)

const checkoutSelectionSelector = createSelector(
  checkoutInfoSelector,
  ({ selection }) => selection
)

export const checkoutSelectedCurrencySelector = createSelector(
  checkoutSelectionSelector,
  ({ currency }) => currency
)

const isPaypalCompatibleCurrencySelector = createSelector(
  checkoutSelectedCurrencySelector,
  (currency) => currency !== 'INR'
)

export const isPaypalAvailableSelector = createSelector(
  featureRootSelector,
  isPaypalCompatibleCurrencySelector,
  (
    { policy },
    isPaypalCompatibleCurrency
  ) => isPaypalCompatibleCurrency && policy && policy.is_paypal_available
)

export const checkoutPostalCodeSelector = createSelector(
  checkoutSelectionSelector,
  ({ postalCode }) => postalCode
)

export const checkoutCountryCodeSelector = createSelector(
  checkoutSelectionSelector,
  ({ countryCode }) => countryCode
)

export const checkoutSelectedAddonsSelector = createSelector(
  checkoutSelectionSelector,
  ({ addons }) => addons
)

export const checkoutAddonsForEstimateSelector = createSelector(
  dealAddonsSelector,
  checkoutSelectedAddonsSelector,
  (dealAddons, addons) => {
    let addonsForCalculation

    if (dealAddons.length > 0) {
      addonsForCalculation = dealAddons.map((addon) => ({ sku_id: addon.id }))
    } else {
      addonsForCalculation = addons.map((skuId) => ({ sku_id: skuId }))
    }

    return addonsForCalculation
  }
)

export const checkoutAddonsForPurchaseSelector = createSelector(
  dealAddonsSelector,
  checkoutSelectedAddonsSelector,
  (dealAddons, addons) => {
    let addonsForCalculation

    if (dealAddons.length > 0) {
      addonsForCalculation = dealAddons.map((addon) => ({ id: addon.id }))
    } else {
      addonsForCalculation = addons.map((skuId) => ({ id: skuId }))
    }

    return addonsForCalculation
  }
)

export const checkoutSelectedPlanSelector = createSelector(
  checkoutSelectionSelector,
  ({ plan }) => plan
)

export const showCurrencyDropdownSelector = createSelector(
  multipleCurrenciesAvailableSelector,
  checkoutSelectedPlanSelector,
  (multipleCurrenciesAvailable, selectedPlan) => (
    multipleCurrenciesAvailable && !NOTETAKER_PLAN_SELECTION_TIERS.includes(selectedPlan)
  )
)

export const checkoutSelectedQuantitySelector = createSelector(
  checkoutSelectionSelector,
  ({ quantity }) => quantity
)

export const checkoutSelectedBillingPeriodSelector = createSelector(
  checkoutSelectionSelector,
  ({ billingPeriod }) => billingPeriod
)

export const checkoutAvailableAddonsSelector = createSelector(
  detailedCheckoutInfoSelector,
  checkoutSelectedBillingPeriodSelector,
  ({ available_addons: availableAddons }, billingPeriod) => availableAddons[billingPeriod]
    .filter((addon) => addon.presentation === 'self_serve')
)

export const checkoutHasAvailableAddonsSelector = createSelector(
  checkoutAvailableAddonsSelector,
  (availableAddons) => (availableAddons?.length || 0) > 0
)

export const checkoutSelectedPaymentProviderSelector = createSelector(
  checkoutSelectionSelector,
  ({ paymentProvider }) => paymentProvider
)

export const checkoutExternalCustomerSelector = createSelector(
  checkoutSelectionSelector,
  ({ externalCustomerId }) => externalCustomerId
)

export const checkoutTaxExemptionSubmittedSelector = createSelector(
  checkoutSelectionSelector,
  ({ taxExemptionSubmitted }) => taxExemptionSubmitted
)

export const checkoutExemptionSubmittedExternalCustomerSelector = createSelector(
  checkoutExternalCustomerSelector,
  checkoutTaxExemptionSubmittedSelector,
  (externalCustomerId, taxExemptionSubmitted) => (taxExemptionSubmitted ? externalCustomerId : null)
)

export const checkoutPurchaseSubmittedSelector = createSelector(
  checkoutSelectionSelector,
  ({ purchaseSubmitted }) => purchaseSubmitted
)

export const checkoutOrderReadyForEstimation = createSelector(
  checkoutSelectedPlanSelector,
  checkoutSelectedQuantitySelector,
  checkoutSelectedBillingPeriodSelector,
  (plan, quantity, billingPeriod) => plan && quantity && billingPeriod && (quantity > 0)
)

export const checkoutEstimateSelector = createSelector(
  checkoutInfoSelector,
  ({ estimate }) => estimate
)

export const checkoutTaxlessEstimateSelector = createSelector(
  checkoutInfoSelector,
  ({ taxlessEstimate }) => taxlessEstimate
)

export const estimateAddonsSelector = createSelector(
  checkoutEstimateSelector,
  (estimate) => estimate?.addons || []
)

export const checkoutFeatureKeySelector = createSelector(
  checkoutInfoSelector,
  ({ featureKey }) => featureKey
)
